import { Image } from 'expo-image';

/**
 * Image cache utility functions for managing expo-image cache
 *
 * expo-image provides automatic memory and disk caching with LRU eviction.
 * These utilities allow manual cache management when needed.
 */

/**
 * Preload images into cache before they're needed
 * Useful for preloading images for the next page in infinite scroll
 *
 * @param uris - Array of image URIs to preload
 */
export async function preloadImages(uris: string[]): Promise<void> {
  try {
    await Promise.all(
      uris.map(uri => Image.prefetch(uri))
    );
  } catch (error) {
    console.error('[imageCache] Error preloading images:', error);
  }
}

/**
 * Clear all cached images from memory and disk
 * Use sparingly - only when user explicitly requests it (e.g., in settings)
 */
export async function clearImageCache(): Promise<void> {
  try {
    await Image.clearMemoryCache();
    await Image.clearDiskCache();
    console.log('[imageCache] Cache cleared successfully');
  } catch (error) {
    console.error('[imageCache] Error clearing cache:', error);
  }
}

/**
 * Get the size of the disk cache in bytes
 * Useful for showing cache size in settings
 */
export async function getCacheSize(): Promise<number> {
  try {
    const size = await Image.getCachePathAsync();
    // Note: This returns the cache directory path, not size
    // For actual size calculation, you'd need to use expo-file-system
    return 0;
  } catch (error) {
    console.error('[imageCache] Error getting cache size:', error);
    return 0;
  }
}
